import { ReactNode, useEffect, useMemo } from 'react'
import {
  FormularioSituacao,
  initialQueryState,
  OptionsHelper,
  useDebounce,
} from '../../../../../../../_metronic/helpers'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  Input,
  IOption,
  TableFilter,
  TableFilters,
} from '../../../../../../components'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'
import { useT } from '../../../../../../../_metronic/i18n/Metronici18n'

type Props = {
  /** Conteúdo à direita da linha dos campos (ex.: barra de download em lote). */
  actions?: ReactNode
}

export const AvaliacoesListSearchComponent = ({ actions }: Props) => {
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { t } = useT()
  const total = response?.totalElements ?? 0

  const debouncedFilters = useDebounce(filters, 500)

  const search = useMemo(
    () =>
      [
        debouncedFilters.protocolo
          ? `atendimento.protocolo~*${debouncedFilters.protocolo.trim()}*`
          : '',
        debouncedFilters.empresa
          ? `atendimento.paciente.empresa:${debouncedFilters.empresa.value}`
          : '',
        debouncedFilters.paciente
          ? `atendimento.paciente.pessoa.nome~*${debouncedFilters.paciente.trim()}*`
          : '',
        debouncedFilters.situacao ? `situacao:${debouncedFilters.situacao.value}` : '',
      ]
        .filter(Boolean)
        .join(','),
    [debouncedFilters]
  )

  useEffect(() => {
    updateState({ ...initialQueryState, search })
  }, [search, updateState])

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo.
  const activeFilters: ActiveFilter[] = [
    filters.paciente.trim() && {
      key: 'paciente',
      label: t('cols.paciente'),
      value: `“${filters.paciente.trim()}”`,
      onRemove: () => setFilter('paciente', ''),
    },
    filters.protocolo.trim() && {
      key: 'protocolo',
      label: t('cols.protocolo'),
      value: `“${filters.protocolo.trim()}”`,
      onRemove: () => setFilter('protocolo', ''),
    },
    filters.empresa && {
      key: 'empresa',
      label: t('empresa.label'),
      value: filters.empresa.label,
      onRemove: () => setFilter('empresa', undefined),
    },
    filters.situacao && {
      key: 'situacao',
      label: t('situacao.label'),
      value: filters.situacao.label,
      onRemove: () => setFilter('situacao', undefined),
    },
  ].filter((filter): filter is ActiveFilter => Boolean(filter))

  // Instituição e situação ficam atrás de "Filtros" (4 campos não cabem bem numa linha).
  const moreFiltersActiveCount = [filters.empresa, filters.situacao].filter(Boolean).length

  return (
    <TableFilters
      actions={actions}
      moreFiltersActiveCount={moreFiltersActiveCount}
      moreFilters={
        <>
          <TableFilter label={t('empresa.label')}>
            <CustomSelect
              entity='empresa'
              fetchOptions={OptionsHelper.getOptionsEmpresas}
              onChange={(empresa?: IOption<number>) => setFilter('empresa', empresa ?? undefined)}
              value={filters.empresa}
            />
          </TableFilter>
          <TableFilter label={t('situacao.label')}>
            <CustomSelect
              placeholder='Selecione uma situação'
              fetchOptions={OptionsHelper.getOptionsFormularioSituacao}
              onChange={(situacao?: IOption<FormularioSituacao>) =>
                setFilter('situacao', situacao ?? undefined)
              }
              value={filters.situacao}
            />
          </TableFilter>
        </>
      }
      footer={
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={clearFilters}
          summary={
            isLoading
              ? 'Buscando…'
              : `${total} ${total === 1 ? 'avaliação encontrada' : 'avaliações encontradas'}`
          }
        />
      }
    >
      <TableFilter label={t('cols.paciente')} wide>
        <Input
          placeholder='Digite o nome do paciente'
          value={filters.paciente}
          onChange={(e) => setFilter('paciente', e.target.value)}
        />
      </TableFilter>
      <TableFilter label={t('cols.protocolo')}>
        <Input
          placeholder='Digite o protocolo'
          value={filters.protocolo}
          onChange={(e) => setFilter('protocolo', e.target.value)}
        />
      </TableFilter>
    </TableFilters>
  )
}
